import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@material-ui/core';
import '../../assets/css/timeline.css';
import * as Utils from '../../commons/utils/Utils.js';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';

/**
 * VEC-3432 Scenario 04 — historial de modificaciones de un ítem de mano de obra.
 *
 * Se auditan los mismos cuatro campos que audita el ABM legacy de Pañol2, que escribe en la misma
 * tabla: costo, costo fijo, activo y valor máximo. Los demás campos no se registran.
 *
 * La presentación replica el timeline del histórico legacy (`components/repuestos/RepuestoHistorico.jsx`):
 * mismas clases de `timeline.css`, header teal, badges y cards, para que quien viene del módulo
 * anterior reconozca la pantalla. Lo que NO se replica son tres flaquezas de esa pantalla: allá el
 * campo `activo` sale crudo, una baja se ve como un guion (el legacy guarda el `false` como cadena
 * vacía) y no hay hora, con lo que dos cambios del mismo día son indistinguibles.
 */
const BASE_URL = '/inventario/mano-obra';

const InventarioManoObraHistorialModal = (props) => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [filas, setFilas] = useState([]);

  const item = props.item;

  useEffect(() => {
    if (!item) {
      setFilas([]);
      return;
    }
    setLoading(true);
    Utils.getData(`${BASE_URL}/${item.id}/historico`)
      .then((data) => setFilas(data || []))
      .catch(() => setFilas([]))
      .finally(() => setLoading(false));
  }, [item]);

  /**
   * El backend devuelve el nombre de la COLUMNA, no la clave del DTO. Se traducen las cuatro que
   * puede haber; cualquier otra se muestra cruda, que es mejor que ocultarla.
   */
  const labelDeCampo = (campo) => {
    switch (campo) {
      case 'costo':
        return intl.formatMessage({ id: 'inventarioManoObra.historial.campo_costo', defaultMessage: 'Costo' });
      case 'costo_fijo':
        return intl.formatMessage({ id: 'inventarioManoObra.historial.campo_costo_fijo', defaultMessage: 'Costo fijo' });
      case 'valor_maximo':
        return intl.formatMessage({ id: 'inventarioManoObra.historial.campo_valor_maximo', defaultMessage: 'Valor máximo' });
      case 'activo':
        return intl.formatMessage({ id: 'inventarioManoObra.historial.campo_activo', defaultMessage: 'Estado' });
      default:
        return campo;
    }
  };

  /**
   * Los valores se guardan con el formato del ABM legacy, que para los booleanos es irregular: un
   * `false` puede estar guardado como '0' o como cadena vacía, según por dónde se hizo el cambio.
   * Las dos formas se muestran igual, así el usuario ve "Inactivo" y no un guion.
   */
  const formatValor = (campo, valor) => {
    if (campo === 'activo') {
      const activo = valor === 1 || valor === '1' || valor === true || valor === 'true';
      return activo
        ? intl.formatMessage({ id: 'inventarioManoObra.historial.activo', defaultMessage: 'Activo' })
        : intl.formatMessage({ id: 'inventarioManoObra.historial.inactivo', defaultMessage: 'Inactivo' });
    }

    if (campo === 'costo_fijo') {
      const si = valor === 1 || valor === '1' || valor === true || valor === 'true';
      return si
        ? intl.formatMessage({ id: 'yes', defaultMessage: 'Si' })
        : intl.formatMessage({ id: 'no', defaultMessage: 'No' });
    }

    if (valor === null || valor === undefined || valor === '') {
      // Para costo y valor máximo, vacío significa "sin valor": en valor_maximo es "sin tope".
      return '-';
    }

    const numero = parseFloat(valor);
    return isNaN(numero) ? String(valor) : LocaleFormat.formatNumber(numero, 2);
  };

  const usuarioDe = (fila) =>
    fila.usuario
      ? fila.usuario
      : intl.formatMessage({
          id: 'inventarioManoObra.historial.usuario_sistema',
          defaultMessage: 'Generado por el sistema',
        });

  /** Card del timeline. Se reusa para las filas y para el estado vacío, así el vacío no desentona. */
  const renderCard = (titulo, detalle, key) => (
    <div className="timeline-card card border-grey border-lighten-2 card-history" key={key}>
      <div className="card-header">
        <h4 className="card-title">{titulo}</h4>
        {detalle && (
          <p className="card-subtitle text-muted pt-1">
            <span className="font-small-3">{detalle}</span>
          </p>
        )}
      </div>
    </div>
  );

  // 1140px es el ancho de `.modal-xl` de Bootstrap, que es el que usa el histórico legacy. Va como
  // estilo inline y no como clase porque Material-UI inyecta sus propias reglas de `max-width` en el
  // head y le ganaría en especificidad a `.modal-xl`.
  return (
    <Dialog
      open={!!item}
      onClose={props.onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ style: { maxWidth: 1140 } }}
    >
      <DialogTitle disableTypography className="modal-header bg-fleet">
        <h4 className="modal-title text-white mb-0">
          <i className="la la-history align-middle icon-modal-title"></i>
          <FormattedMessage
            id="inventarioManoObra.historial.titulo"
            defaultMessage=" Historial del ítem de Mano de Obra: "
          />
          {item ? item.descripcion : ''}
        </h4>
      </DialogTitle>

      <DialogContent dividers className="modal-grey">
        {loading && (
          <div className="text-center my-3">
            <CircularProgress />
          </div>
        )}

        {!loading && (
          <div className="content-wrapper">
            <div className="content-body">
              <section id="timeline" className="timeline-left timeline-wrapper">
                <ul className="timeline">
                  <li className="timeline-line"></li>

                  {filas.length === 0 && (
                    <li className="timeline-item">
                      {renderCard(
                        intl.formatMessage({
                          id: 'inventarioManoObra.historial.sin_cambios',
                          defaultMessage: 'Este ítem no tiene modificaciones registradas.',
                        }),
                        null,
                        'sin_cambios'
                      )}
                    </li>
                  )}

                  {filas.map((fila, indice) => {
                    const campoLabel = labelDeCampo(fila.campo);
                    const esCambioCosto = fila.campo === 'costo';

                    // formatDateTime y no formatDate: en un historial, dos cambios del mismo día
                    // quedarían indistinguibles sin la hora (el legacy tiene justamente ese problema).
                    const fecha = fila.fechaHora ? LocaleFormat.formatDateTime(fila.fechaHora) : '-';

                    const titulo =
                      fecha +
                      ' - ' +
                      intl.formatMessage(
                        { id: 'inventarioManoObra.historial.evento_cambio', defaultMessage: 'Cambio de {campo}' },
                        { campo: campoLabel }
                      ) +
                      ' - ' +
                      usuarioDe(fila);

                    const detalle = intl.formatMessage(
                      { id: 'inventarioManoObra.historial.detalle', defaultMessage: '{campo}: {anterior} → {nuevo}' },
                      {
                        campo: campoLabel,
                        anterior: formatValor(fila.campo, fila.valorAnterior),
                        nuevo: formatValor(fila.campo, fila.valorNuevo),
                      }
                    );

                    return (
                      <li
                        key={'historial_' + indice}
                        className={esCambioCosto ? 'timeline-item status-change' : 'timeline-item'}
                      >
                        <div className="timeline-badge">
                          <span className="bg-fleet bg-lighten-1" data-toggle="tooltip" data-placement="right">
                            <i className={esCambioCosto ? 'la la-usd' : 'la la-history'}></i>
                          </span>
                        </div>
                        {renderCard(titulo, detalle, 'card_' + indice)}
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>
          </div>
        )}

        <div className="text-muted mt-2" style={{ fontSize: 12 }}>
          <FormattedMessage
            id="inventarioManoObra.historial.ayuda_campos_auditados"
            defaultMessage="Se registran los cambios de costo, costo fijo, valor máximo y estado."
          />
        </div>
      </DialogContent>

      <DialogActions className="modal-grey">
        <button type="button" className="btn btn-default btn-fleet" onClick={props.onClose}>
          <FormattedMessage id="Ok" defaultMessage="Ok" />
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default InventarioManoObraHistorialModal;
